import { Add } from "@mui/icons-material";
import { Fab, Grid } from "@mui/material";
import DataTable from "app/components/DataTable";
import { TabPanel } from "app/components/TabPanel";
import { UploadedFileCell } from "app/components/UploadedFileCell";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { selectFeaturePermissions } from "store/global/authPermission/selector";
import { PermissionFeature } from "store/global/authPermission/types";
import { MAX_LIMIT } from "utils/helpers";
import toastService from "utils/toast";
import AddTreatmentContent from "../../components/AddTreatmentContent";
import {
  selectEditTreatment,
  selectList,
  selectLoading,
  selectQuery,
  selectcontentModal,
} from "../../redux/selector";
import { actions } from "../../redux/slice";

type Props = {
  mode: "new" | "edit" | "view";
  value: number;
  disable: boolean;
  disabledOnAssociation: boolean;
};
const TreatmentList = ({
  mode,
  value,
  disable,
  disabledOnAssociation,
}: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const [onRow, setOnRow] = useState(true);
  const [edit, setEdit] = useState(mode);
  const featurePermissions = useSelector(selectFeaturePermissions);
  const { hasFullAccess, hasReadAccess, hasNoAccess } =
    featurePermissions[PermissionFeature.TREATMENT_TYPES];

  const [editIndex, setEditIndex] = useState(0);
  const listitems = useSelector(selectList);
  const contentModal = useSelector(selectcontentModal);

  const { treatment } = listitems;

  const { orderField, orderDirection } = useSelector(selectQuery);

  const form = useSelector(selectEditTreatment);

  const setPageQuery = (name: string, value: any) => {
    dispatch(actions.setQuery({ name, value }));
  };
  const columns = [
    {
      id: "beforeAfter",
      label: t("TREATMENT_TYPES.BEFORE_AFTER"),
      sortValue: "selector.beforeafter",
      align: "left",
    },
    {
      id: "hours",
      label: t("TREATMENT_TYPES.HOURS"),
      sortValue: "selector.hours",
      align: "left",
    },
    {
      id: "category",
      label: t("TREATMENT_TYPES.CATEGORY"),
      sortValue: "selector.category",
      align: "left",
      format: (val) => val?.category || "-",
    },
    {
      id: "uploadedFile",
      label: t("TREATMENT_TYPES.UPLOADED_FILE_HEADER"),
      align: "left",
      format: (val) => (
        <UploadedFileCell resources={val?.resources ?? []} />
      ),
    },
    {
      id: "shortDescription",
      label: t("TREATMENT_TYPES.SHORT_DESCRIPTION"),
      sortValue: "selector.shortDescription",
      align: "left",
      format: (val) => val?.shortDescription || "-",
    },
    {
      id: "description",
      label: t("TREATMENT_TYPES.DESCRIPTION"),
      sortValue: "selector.description",
      align: "left",
    },
    {
      id: "session_id",
      label: t("TREATMENT_TYPES.SESSION"),
      sortValue: "selector.session_id",
      align: "left",
    },
  ];

  const dispatch = useDispatch();
  const loading = useSelector(selectLoading);

  const handleModalContent = () => {
    setEdit("new");
    if (treatment.length < 10) {
      setOnRow(false);
      dispatch(actions.contentModalToggal());
    } else {
      toastService.error("Only 10 Treatment Content Allowed ");
    }
  };
  const handleModalContentClose = () => {
    dispatch(actions.contentModalToggal());
    dispatch(actions.resetContentForm());
  };

  function handleTreatmentContentDeletion(row, index) {
    if (mode !== "view") {
      const { id, name } = row;
      handleDeleteClose("delete", "", id, index);
    }
  }

  const handleDeleteClose = (
    eventType,
    type?: string,
    id?: number,
    index?: number
  ) => {
    if (eventType === "delete") {
      if (id == 0) {
        dispatch(actions.deleteTreatmentContentItem(index));
      } else {
        dispatch(
          actions.deleteTreatmentContentRow({
            id: id,
            treatmentId: id,
          })
        );
      }
    }
  };
  const rowDoubleAction = (e, index) => {
    setEdit("edit");
    for (let i in e) {
      if (i === "resources") {
        dispatch(
          actions.updateFormResourseValue({
            key: `content.resources`,
            value: e.resources ?? [],
          })
        );
      } else {
        dispatch(actions.updateFormValue({ key: `content.${i}`, value: e[i] }));
      }
    }

    setOnRow(true);
    setEditIndex(index);
    navigate(`/treatment-types/${id}/edit`);
    dispatch(actions.contentModalToggal());
  };

  return (
    <>
      <TabPanel value={value} index={0}>
        <Grid container spacing={0} md={12} sx={{ paddingTop: "0px" }}>
          <Grid item xs={12}>
            <DataTable
              loading={loading}
              orderDirection={orderDirection}
              orderField={orderField}
              setQuery={setPageQuery}
              rows={treatment.filter((x) => x.type !== "delete")}
              columns={columns}
              Actions={hasFullAccess ? "Delete,Edit" : ""}
              editAction={rowDoubleAction}
              deleteAction={handleTreatmentContentDeletion}
              disableActions={
                disable || disabledOnAssociation ? "Delete,Edit" : ""
              }
            />
          </Grid>
        </Grid>
        {treatment?.length < MAX_LIMIT && (
          <Grid
            xs={12}
            style={{
              display: "flex",
              justifyContent: "flex-end",
              paddingTop: "7px",
            }}
          >
            <Fab
              size="medium"
              color="secondary"
              aria-label="new"
              sx={{ marginTop: 2 }}
              disabled={disable}
              onClick={handleModalContent}
            >
              <Add />
            </Fab>
          </Grid>
        )}
      </TabPanel>
      <AddTreatmentContent
        treatmentId={id}
        openModal={contentModal}
        handleModalClose={handleModalContentClose}
        disable={disable}
        mode={edit}
        noofSession={form.noOfSession.value}
        editIndex={editIndex}
      ></AddTreatmentContent>
    </>
  );
};
export default TreatmentList;
